import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PublishResultCard from "./PublishResultCard";
import {
  buildSupportText,
  PUBLISH_ERROR_HEADLINES,
  PUBLISH_ERROR_HINTS,
  PUBLISH_UNKNOWN_CODE,
  PublishFailure,
  toneForCode,
} from "./publishErrors";

interface PublishErrorCardProps {
  failure: PublishFailure;
  trainingName?: string;
  organizationName?: string;
  organizationId?: string;
}

export default function PublishErrorCard(props: PublishErrorCardProps) {
  const { failure, trainingName, organizationName, organizationId } = {
    ...props,
  };
  const { t } = useTranslation();
  const isRetryable = toneForCode(failure.code) === "retryable";
  const hint = PUBLISH_ERROR_HINTS[failure.code];

  const onCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(
        buildSupportText(failure, {
          organizationName,
          organizationId,
          trainingName,
        })
      );
      toast.success(t("publish_page.copied", { defaultValue: "Details copied" }), {
        position: "top-right",
        autoClose: 2500,
      });
    } catch {
      toast.error(
        t("publish_page.copy_failed", {
          defaultValue: "Could not copy the details",
        }),
        { position: "top-right", autoClose: 3500 }
      );
    }
  };

  const header = (
    <>
      <span
          className={classNames({
            "rounded-[4px] px-[8px] py-[4px] font-mono text-[11px] [overflow-wrap:anywhere]":
              true,
            "bg-creator-error-washed text-creator-error": !isRetryable,
            "bg-[var(--pi-key-yellow-faded)] text-[var(--pi-key-yellow)]":
              isRetryable,
          })}
        >
          {failure.code}
        </span>
        <span className="rounded-[4px] bg-creator-childActive px-[8px] py-[4px] font-mono text-[11px] text-creator-text-sub">
          {failure.status === 0
            ? t("publish_page.no_response_status", {
                defaultValue: "no response",
              })
            : `HTTP ${failure.status}`}
        </span>
        <div className="ml-auto">
          <button
            type="button"
            onClick={onCopy}
            className="flex cursor-pointer items-center gap-[6px] rounded-[6px] border border-solid border-creator-border bg-creator-surface px-[10px] py-[6px] font-poppins text-[12px] font-medium text-creator-text-sub hover:bg-creator-childActive"
          >
            <ContentCopyOutlinedIcon color="inherit" sx={{ fontSize: 14 }} />
            {t("publish_page.copy_details", { defaultValue: "Copy details" })}
          </button>
        </div>
    </>
  );

  return (
    <PublishResultCard header={header}>
      <div className="flex items-start gap-[12px] p-[16px]">
        <div
          className={classNames({
            // fixed 20px box so the icon centres on the headline's first line
            // instead of floating inside an inherited line box
            "flex h-[20px] w-[20px] shrink-0 items-center justify-center text-[20px] leading-none":
              true,
            "text-creator-error": !isRetryable,
            "text-[var(--pi-key-yellow)]": isRetryable,
          })}
        >
          {isRetryable ? (
            <WarningAmberOutlinedIcon color="inherit" fontSize="inherit" />
          ) : (
            <ErrorOutlineOutlinedIcon color="inherit" fontSize="inherit" />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
          <div className="text-14 font-medium leading-[1.45] text-creator-text">
            {t(`publish_page.errors.${failure.code}`, {
              defaultValue:
                PUBLISH_ERROR_HEADLINES[failure.code] ??
                PUBLISH_ERROR_HEADLINES[PUBLISH_UNKNOWN_CODE],
            })}
          </div>

          {hint && (
            <div className="text-[12.5px] leading-[1.55] text-creator-text-sub">
              {t(`publish_page.error_hints.${failure.code}`, {
                defaultValue: hint,
              })}
            </div>
          )}

          {failure.message && (
            <div className="flex min-w-0 flex-col gap-[6px] rounded-[6px] bg-[var(--pi-surface-hover)] px-[12px] py-[10px]">
              <div className="text-[10.5px] font-medium text-creator-text-soft">
                {t("publish_page.server_message", {
                  defaultValue: "Server message",
                })}
              </div>
              <div className="min-w-0 font-mono text-[11.5px] leading-[1.6] text-creator-text-sub whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {failure.message}
              </div>
            </div>
          )}
        </div>
      </div>
    </PublishResultCard>
  );
}
